(() => {
  const SHARED_PARAM_KEYS = ['preview_theme_id', '_fd', 'pb', 'locale', 'currency'];
  const MAX_RECENT_SEARCHES = 5;
  const MAX_VIEWED_PRODUCTS = 4;
  const HOME_BUY_PAGE_SIZE = 20;
  const HOME_MARKET_INITIAL_RECORD_COUNT = 20;
  const HOME_MARKET_RETRY_MS = 30000;
  const marketRootStates = new WeakMap();
  const HOME_BUY_PAGINATION_WINDOW = 5;
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const currencyFormatter = new Intl.NumberFormat('ja-JP');

  function getMarketPlaceholderUrl() {
    return document.querySelector('[data-ts-home-hub]')?.dataset.marketPlaceholderUrl || '';
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getSafeStorage() {
    try {
      return window.localStorage;
    } catch (error) {
      return null;
    }
  }

  function buildCollectionUrl(root, { query = '', inventory = '' } = {}) {
    const normalizedQuery = (query || '').trim();
    const selectedInventory = inventory || root.dataset.defaultInventory || 'market';
    if (normalizedQuery && normalizedQuery.length <= 100 && selectedInventory === 'market'
        && root.dataset.dbSearchEnabled === 'true' && root.dataset.searchUrl) {
      const searchUrl = new URL(root.dataset.searchUrl, window.location.origin);
      if (searchUrl.origin === window.location.origin && !searchUrl.search && !searchUrl.hash
          && /^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/)?search$/i.test(searchUrl.pathname)) {
        getSharedSearchParams().forEach((value, key) => searchUrl.searchParams.set(key, value));
        searchUrl.searchParams.set('q', normalizedQuery);
        return `${searchUrl.pathname}${searchUrl.search}`;
      }
    }
    const collectionUrl = root.dataset.collectionUrl || '/collections/all';
    const url = new URL(collectionUrl, window.location.origin);
    const queryParam = root.dataset.queryParam || 'tsf_q';
    const inventoryParam = root.dataset.inventoryParam || 'tsInventory';
    const defaultInventory = root.dataset.defaultInventory || 'market';
    const defaultAvailability = root.dataset.defaultAvailability || 'in-stock';
    const defaultSort = root.dataset.defaultSort || 'created-descending';
    const sharedSearchParams = getSharedSearchParams();

    sharedSearchParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    url.searchParams.set(inventoryParam, inventory || defaultInventory);
    url.searchParams.set('tsf_availability', defaultAvailability);
    url.searchParams.set('sort_by', defaultSort);

    if (normalizedQuery) {
      url.searchParams.set(queryParam, normalizedQuery);
    } else {
      url.searchParams.delete(queryParam);
    }

    return `${url.pathname}${url.search}`;
  }

  function buildEntityUrl(href, pageType) {
    if (!['brand', 'model', 'ref'].includes(pageType) || typeof href !== 'string') return null;
    try {
      const url = new URL(href, window.location.origin);
      const pattern = new RegExp(`^/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*/)?collections/${pageType}-[a-z0-9][a-z0-9-]{0,250}$`, 'i');
      if (url.origin !== window.location.origin || url.username || url.password || url.search || url.hash
          || !pattern.test(url.pathname)) return null;
      getSharedSearchParams().forEach((value, key) => url.searchParams.set(key, value));
      return `${url.pathname}${url.search}`;
    } catch (_) {
      return null;
    }
  }

  window.TimeseekHomeSearch = { buildCollectionUrl, buildEntityUrl };

  function getSharedSearchParams() {
    const source = new URLSearchParams(window.location.search);
    const result = new URLSearchParams();

    SHARED_PARAM_KEYS.forEach((key) => {
      if (source.has(key)) {
        result.set(key, source.get(key));
      }
    });

    return result;
  }

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (element.hasAttribute('hidden')) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      return true;
    });
  }

  function setTabButtonState(buttons, activeValue, valueAccessor) {
    buttons.forEach((button) => {
      const value = valueAccessor(button);
      const isActive = value === activeValue;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  function setPressedButtonState(buttons, activeValue, valueAccessor) {
    buttons.forEach((button) => {
      const value = valueAccessor(button);
      const isActive = value === activeValue;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function bindArrowKeyNavigation(buttons, onSelect) {
    buttons.forEach((button, index) => {
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();

        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + buttons.length) % buttons.length;
        const nextButton = buttons[nextIndex];
        nextButton.focus();
        onSelect(nextButton);
      });
    });
  }

  function formatPrice(value) {
    const numericValue = typeof value === 'number' ? value : Number.parseInt(String(value || '').replace(/[^\d]/g, ''), 10);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return '価格は商品ページで確認';
    return `¥${currencyFormatter.format(numericValue)}`;
  }

  function normalizePriceValue(value, item = {}) {
    if (item.marketListing === true && typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    const rawValue = String(value || '').trim();
    if (!rawValue) return '';

    const numericValue = Number.parseInt(rawValue.replace(/[^\d]/g, ''), 10);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return rawValue;

    const priceUnit = String(item.priceUnit || item.price_unit || '').trim().toLowerCase();
    if (priceUnit === 'subunits') {
      return Math.round(numericValue / 100);
    }

    if (priceUnit === 'yen') {
      return numericValue;
    }

    const hasVariantId = Boolean(String(item.variantId || item.variant_id || '').trim());
    if (hasVariantId && numericValue >= 100000000 && numericValue % 100 === 0) {
      return Math.round(numericValue / 100);
    }

    return numericValue;
  }

  function buildDisplayTitle(item = {}) {
    const brand = String(item.brand || '').trim();
    const baseTitle = String(
      item.displayTitle || item.title || item.model || [item.brand, item.reference].filter(Boolean).join(' ')
    )
      .replace(/\s+/g, ' ')
      .trim();

    if (!brand) return baseTitle;
    if (!baseTitle) return brand;

    const normalizedTitle = baseTitle.toLowerCase();
    const normalizedBrand = brand.toLowerCase();
    if (normalizedTitle.includes(normalizedBrand)) return baseTitle;

    return `${brand} ${baseTitle}`.trim();
  }

  function normalizeImageUrl(value) {
    const rawValue = String(value || '').trim();
    if (!rawValue) return '';

    try {
      const url = new URL(rawValue, window.location.origin);
      if (rawValue.startsWith('//')) {
        return `${url.pathname}${url.search}`.startsWith('//') ? rawValue : `//${url.host}${url.pathname}${url.search}`;
      }
      if (rawValue.startsWith('/')) {
        return `${url.pathname}${url.search}`;
      }
      return url.toString();
    } catch (error) {
      return rawValue;
    }
  }

  function optimizeMarketImageUrl(value) {
    const normalizedValue = normalizeImageUrl(value);
    if (!normalizedValue) return '';

    let optimizedValue = normalizedValue;

    try {
      const url = new URL(optimizedValue, window.location.origin);
      const hostname = String(url.hostname || '').toLowerCase();

      if (hostname.includes('thumbnail.image.rakuten.co.jp')) {
        url.searchParams.set('_ex', '900x900');
      }

      if (hostname.includes('item-shopping.c.yimg.jp')) {
        url.pathname = url.pathname.replace(/\/i\/[a-z]\//i, '/i/l/');
      }

      optimizedValue = url.toString();
    } catch (error) {
      return normalizedValue;
    }

    const shouldTrimViaProxy =
      optimizedValue.includes('thumbnail.image.rakuten.co.jp') ||
      /\.(?:jpe?g|png|webp)(?:\?|$)/i.test(optimizedValue);

    if (shouldTrimViaProxy && !optimizedValue.includes('wsrv.nl/?url=')) {
      const encodedUrl = encodeURIComponent(optimizedValue);
      return `https://wsrv.nl/?url=${encodedUrl}&trim=12&w=1200&h=1320&fit=contain&bg=ffffff&a=center&output=webp`;
    }

    return optimizedValue;
  }

  function buildSizedShopifyImageUrl(value, width) {
    const rawValue = String(value || '').trim();
    if (!rawValue || !Number.isFinite(width) || width <= 0) return normalizeImageUrl(rawValue);

    try {
      const url = new URL(rawValue, window.location.origin);
      const isShopifyCdn = url.pathname.includes('/cdn/shop/');
      if (!isShopifyCdn) return normalizeImageUrl(rawValue);

      url.searchParams.set('width', String(width));

      if (rawValue.startsWith('//')) {
        return `//${url.host}${url.pathname}${url.search}`;
      }
      if (rawValue.startsWith('/')) {
        return `${url.pathname}${url.search}`;
      }
      return url.toString();
    } catch (error) {
      return normalizeImageUrl(rawValue);
    }
  }

  function buildResponsiveImageAttributes(value) {
    const normalizedValue = optimizeMarketImageUrl(value);
    if (!normalizedValue) {
      return {
        src: '',
        srcset: '',
        sizes: '',
      };
    }

    const isShopifyCdn = normalizedValue.includes('/cdn/shop/');
    if (!isShopifyCdn) {
      return {
        src: normalizedValue,
        srcset: '',
        sizes: '',
      };
    }

    const widths = [480, 720, 960, 1320, 1680];
    return {
      src: buildSizedShopifyImageUrl(normalizedValue, 1680),
      srcset: widths.map((width) => `${buildSizedShopifyImageUrl(normalizedValue, width)} ${width}w`).join(', '),
      sizes:
        '(min-width: 990px) calc((100vw - 140px) / 4), (min-width: 750px) calc((100vw - 96px) / 2), calc(100vw - 48px)',
    };
  }

  function buildPaginationTokens(totalPages, currentPage) {
    if (totalPages <= HOME_BUY_PAGINATION_WINDOW + 2) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const tokens = [1];
    const windowStart = Math.max(2, currentPage - 1);
    const windowEnd = Math.min(totalPages - 1, currentPage + 1);

    if (windowStart > 2) tokens.push('...');
    for (let page = windowStart; page <= windowEnd; page += 1) {
      tokens.push(page);
    }
    if (windowEnd < totalPages - 1) tokens.push('...');

    tokens.push(totalPages);
    return tokens;
  }

  function buildProductCardMarkup(item) {
    const metaParts = [item.brand, item.model].filter(Boolean);
    const sourceLabel = item.sourceLabel || item.source_label || '';
    const title = buildDisplayTitle(item);
    const reference = item.reference || '';
    const image = item.image || '';
    const href = item.href || '#';
    const productId = item.productId || item.product_id || '';
    const variantId = item.variantId || item.variant_id || '';
    const handle = item.handle || '';
    const priceValue = normalizePriceValue(item.price, item);
    const priceUnit = item.priceUnit || item.price_unit || (priceValue ? 'yen' : '');
    const badgeLabel = item.badgeLabel || '';
    const badgeClass = item.badgeModifier ? ` ts-home-product-card__source--${escapeHtml(item.badgeModifier)}` : '';
    const escapedTitle = escapeHtml(title);
    const escapedHref = escapeHtml(href);
    const escapedReference = escapeHtml(reference);
    const imageAttributes = buildResponsiveImageAttributes(image);
    const escapedImage = escapeHtml(imageAttributes.src || image);
    const placeholderImage = getMarketPlaceholderUrl();
    const placeholderOnError = placeholderImage
      ? ` onerror="this.onerror=null;this.removeAttribute('srcset');this.src='${escapeHtml(
          placeholderImage
        )}';this.classList.remove('ts-home-product-card__image--market','ts-home-product-card__image--market-trimmed','ts-home-product-card__image--market-raw');this.classList.add('ts-home-product-card__image--placeholder');var s=this.closest('.ts-home-product-card__media-stage');if(s){s.classList.remove('ts-home-product-card__media-stage--market-trimmed','ts-home-product-card__media-stage--market-raw');}"`
      : '';
    const responsiveImageAttributes = imageAttributes.srcset
      ? ` srcset="${escapeHtml(imageAttributes.srcset)}" sizes="${escapeHtml(imageAttributes.sizes)}"`
      : '';
    const normalizedImageSource = String(imageAttributes.src || image || '').trim();
    const isNativeImage = normalizedImageSource.includes('/cdn/shop/');
    const isTrimmedMarketImage = !isNativeImage && normalizedImageSource.includes('wsrv.nl/?url=');
    const mediaStageClass = isNativeImage
      ? 'ts-home-product-card__media-stage ts-home-product-card__media-stage--native'
      : `ts-home-product-card__media-stage ts-home-product-card__media-stage--market ${isTrimmedMarketImage ? 'ts-home-product-card__media-stage--market-trimmed' : 'ts-home-product-card__media-stage--market-raw'}`;
    const imageClass = isNativeImage
      ? 'ts-home-product-card__image ts-home-product-card__image--native'
      : `ts-home-product-card__image ts-home-product-card__image--market ${isTrimmedMarketImage ? 'ts-home-product-card__image--market-trimmed' : 'ts-home-product-card__image--market-raw'}`;
    const cardDataAttributes = [
      ['data-ts-home-product-card', 'true'],
      ['data-product-id', productId],
      ['data-variant-id', variantId],
      ['data-product-handle', handle],
      ['data-product-url', href],
      ['data-product-title', title],
      ['data-product-brand', item.brand || ''],
      ['data-product-model', item.model || ''],
      ['data-product-source', sourceLabel],
      ['data-product-image', image],
      ['data-product-price', priceValue],
      ['data-product-price-unit', priceUnit],
      ['data-product-ref', reference],
      ['data-product-badge', badgeLabel],
    ]
      .filter(([, value]) => value !== '')
      .map(([name, value]) => `${name}="${escapeHtml(value)}"`)
      .join(' ');
    const metaMarkup = sourceLabel
      ? `<p class="ts-home-product-card__meta">${escapeHtml(sourceLabel)}</p>`
      : metaParts.length
      ? `<p class="ts-home-product-card__meta">${metaParts.map((part) => escapeHtml(part)).join('<span>/</span>')}</p>`
      : '';
    const badgeMarkup = badgeLabel
      ? `<span class="ts-home-product-card__source${badgeClass}">${escapeHtml(badgeLabel)}</span>`
      : '';

    return `
      <article class="ts-home-product-card">
        <a class="ts-home-product-card__link" href="${escapedHref}" ${cardDataAttributes}>
          <div class="ts-home-product-card__media">
            <div class="${mediaStageClass}">
              <img class="${imageClass}" src="${escapedImage}" alt="${escapedTitle}" loading="lazy" decoding="async" width="1200" height="1320"${placeholderImage ? ` data-ts-placeholder-src="${escapeHtml(placeholderImage)}"` : ''}${placeholderOnError}${responsiveImageAttributes}>
            </div>
            ${badgeMarkup}
          </div>
          <div class="ts-home-product-card__body">
            ${metaMarkup}
            <h3 class="ts-home-product-card__title">${escapedTitle}</h3>
            ${reference ? `<p class="ts-home-product-card__reference">${escapedReference}</p>` : ''}
            <p class="ts-home-product-card__price">${formatPrice(priceValue)}</p>
          </div>
        </a>
      </article>
    `;
  }

  function readMarketListing(item) {
    const brand = item.brand || '';
    const model = item.model || '';
    const reference = item.reference || '';
    const title = [brand, model, reference].filter(Boolean).join(' ');
    const sourceLabel = item.source === 'rakuten' ? '楽天市場'
      : item.source === 'yahoo_shopping' ? 'Yahoo!ショッピング'
      : item.shop_name || item.source || 'マーケット';
    return {
      href: item.product_url,
      image: item.image_url || getMarketPlaceholderUrl(),
      title,
      displayTitle: [brand, model].filter(Boolean).join(' ') || reference,
      brand,
      model,
      sourceLabel,
      reference,
      price: item.price,
      priceUnit: 'yen',
      marketListing: true,
      createdAt: item.created_at * 1000,
      available: item.in_stock,
      badgeLabel: 'PR',
      badgeModifier: 'pr',
    };
  }

  function projectMarketHomeListings(listings) {
    return listings.filter((item) => item.in_stock)
      .sort((left, right) => right.created_at - left.created_at || left.id.localeCompare(right.id))
      .slice(0, HOME_MARKET_INITIAL_RECORD_COUNT).map(readMarketListing);
  }

  function getHomeMarketState(root) {
    const manifestUrl = root.dataset.marketListingsManifestUrl || '';
    let state = marketRootStates.get(root);
    if (!state || state.manifestUrl !== manifestUrl) {
      state = { manifestUrl, client: null, verified: false, items: [], pending: null, retryAt: 0, refreshFailed: false, ssrAdopted: false };
      marketRootStates.set(root, state);
      const api = window.TimeseekMarketListings;
      if (manifestUrl && typeof api?.readBootstrap === 'function' && typeof api?.createClient === 'function') {
        try {
          const bootstrap = api.readBootstrap(root, 'home');
          if (bootstrap) {
            const client = api.createClient(manifestUrl, { bootstrap, freshness: 'last-known' });
            if (!client.getFreshness()) throw new Error('Market listings bootstrap was not verified');
            state.client = client;
            state.items = projectMarketHomeListings(bootstrap.items);
            state.ssrAdopted = true;
          }
        } catch (error) {
          console.warn('ts home hub bootstrap unavailable:', error);
        }
      }
    }
    return state;
  }

  function updateHomeMarketNotice(root, status, state) {
    const freshness = state.verified || state.ssrAdopted ? state.client?.getFreshness?.() : null;
    if (!freshness) return;
    const snapshotState = freshness.stale ? 'stale' : 'fresh';
    const refreshFailed = state.refreshFailed || freshness.refresh_failed;
    root.dataset.tsMarketSnapshotState = snapshotState;
    root.dataset.tsMarketSnapshotGeneratedAt = freshness.generated_at;
    root.dataset.tsMarketSnapshotExpiresAt = freshness.expires_at;
    root.dataset.tsMarketSnapshotRefreshFailed = String(Boolean(refreshFailed));
    if (status.dataset) {
      status.dataset.snapshotState = snapshotState;
      status.dataset.generatedAt = freshness.generated_at;
      status.dataset.expiresAt = freshness.expires_at;
      status.dataset.refreshFailed = String(Boolean(refreshFailed));
    }
    status.hidden = false;
    status.setAttribute?.('aria-live', 'polite');
    // Keep the observation date from the verified generation, including when stale.
    const date = new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      hourCycle: 'h23', timeZone: 'Asia/Tokyo',
    }).format(new Date(freshness.generated_at));
    status.textContent = `最終更新: ${date}${state.items.length ? '' : '\n現在表示できるマーケット在庫はありません。'}`;
  }

  async function loadMarketHomeCards(root, track, status, onUpdate) {
    const state = getHomeMarketState(root);
    updateHomeMarketNotice(root, status, state);
    if (state.pending) return state.pending;
    if (Date.now() < state.retryAt) return;
    if (state.verified && !state.refreshFailed && !state.client.isRefreshDue()) return;
    const api = window.TimeseekMarketListings;
    if (!state.manifestUrl || typeof api?.createClient !== 'function') {
      if (!state.verified) status.textContent = 'マーケット在庫を読み込めませんでした。';
      return;
    }
    state.pending = Promise.resolve().then(async () => {
      try {
        if (state.ssrAdopted && !state.verified) {
          try {
            // Seed shape is synchronous; its manifest SHA still needs WebCrypto.
            await state.client.recent();
            state.verified = true;
          } catch (error) {
            console.warn('ts home hub bootstrap integrity failed:', error);
            state.ssrAdopted = false;
            state.client = null;
            state.items = [];
            ['tsMarketSnapshotState', 'tsMarketSnapshotGeneratedAt', 'tsMarketSnapshotExpiresAt',
              'tsMarketSnapshotRefreshFailed'].forEach((key) => { delete root.dataset[key]; });
            if (status.dataset) {
              ['snapshotState', 'generatedAt', 'expiresAt', 'refreshFailed']
                .forEach((key) => { delete status.dataset[key]; });
            }
            if (typeof onUpdate === 'function') onUpdate([], { complete: false });
          }
        }
        if (!state.verified) status.textContent = 'マーケット在庫を読み込み中です。';
        const current = state.client;
        if (state.verified && !state.refreshFailed && !current.isRefreshDue()) {
          updateHomeMarketNotice(root, status, state);
          return;
        }
        const client = state.verified ? await current.refresh()
          : (state.client ||= api.createClient(state.manifestUrl, { freshness: 'last-known' }));
        if (state.verified && client === current) {
          state.refreshFailed = false;
          updateHomeMarketNotice(root, status, state);
          return;
        }
        const listings = await client.recent();
        const items = projectMarketHomeListings(listings);
        // Commit the client and its cards together only after recent has passed verification.
        state.client = client;
        state.items = items;
        state.verified = true;
        state.ssrAdopted = false;
        state.refreshFailed = false;
        state.retryAt = 0;
        updateHomeMarketNotice(root, status, state);
        if (typeof onUpdate === 'function') onUpdate(items, { complete: true });
      } catch (error) {
        console.warn('ts home hub market load failed:', error);
        state.refreshFailed = true;
        state.retryAt = Date.now() + HOME_MARKET_RETRY_MS;
        if (state.verified) updateHomeMarketNotice(root, status, state);
        else status.textContent = 'マーケット在庫の読み込みに失敗しました。';
      } finally {
        state.pending = null;
      }
    });
    return state.pending;
  }

  function initRoot(root) {
    // Keep actual entity destinations usable with JS disabled. Only the allowed
    // preview/locale parameters are added to already verified SSR destinations.
    root.querySelectorAll('[data-ts-home-entity-link]').forEach((link) => {
      const url = buildEntityUrl(link.getAttribute('href'), link.dataset.tsHomeEntityType);
      if (url) link.setAttribute('href', url);
    });
    window.TimeseekEntityRecommendations?.init(root, { buildEntityUrl });
    const storage = getSafeStorage();
    const historyKey = root.dataset.historyKey || 'timeseek:home-search-history';
    const viewedKey = root.dataset.viewedKey || 'timeseek:recently-viewed:v1';
    const queryDisplay = root.querySelector('[data-ts-home-query-display]');
    const submitButton = root.querySelector('[data-ts-home-submit]');
    const inventoryButtons = Array.from(root.querySelectorAll('[data-ts-home-inventory-toggle]'));
    const sheet = root.querySelector('[data-ts-home-search-sheet]');
    const dialog = root.querySelector('[data-ts-home-sheet-dialog]');
    const openButtons = Array.from(root.querySelectorAll('[data-ts-home-sheet-open]'));
    const closeButtons = Array.from(root.querySelectorAll('[data-ts-home-sheet-close]'));
    const searchForm = root.querySelector('[data-ts-home-search-form]');
    const searchInput = root.querySelector('[data-ts-home-search-input]');
    const recentWrap = root.querySelector('[data-ts-home-recent-wrap]');
    const recentList = root.querySelector('[data-ts-home-recent-list]');

    const exploreButtons = Array.from(root.querySelectorAll('[data-ts-home-panel-tab]'));
    const explorePanels = Array.from(root.querySelectorAll('[data-ts-home-panel]'));

    const viewedSection = root.querySelector('[data-ts-home-viewed]');
    const viewedTrack = root.querySelector('[data-ts-home-viewed-track]');

    const buyNowButtons = Array.from(root.querySelectorAll('[data-ts-home-buy-tab]'));
    const buyNowPanels = Array.from(root.querySelectorAll('[data-ts-home-buy-panel]'));
    const buyNowLinks = Array.from(root.querySelectorAll('[data-ts-home-buy-now-link], [data-ts-home-buy-now-footer-link]'));
    const buyNowPagination = root.querySelector('[data-ts-home-buy-pagination]');
    const marketTrack = root.querySelector('[data-ts-home-market-track]');
    const marketStatus = root.querySelector('[data-ts-home-market-status]');
    const timeseekTrack = root.querySelector('[data-ts-home-timeseek-track]');

    let currentInventory = root.dataset.defaultInventory || 'market';
    let currentQuery = '';
    let isSheetOpen = false;
    let lastFocusedElement = null;
    let marketLoadPromise = null;
    let currentBuyNowPanel = 'market';
    let marketItems = getHomeMarketState(root).items;
    let timeseekItems = [];
    const buyNowPageState = {
      market: 1,
      timeseek: 1,
    };

    function readRecentSearches() {
      if (!storage) return [];
      try {
        const rawValue = storage.getItem(historyKey);
        const parsed = JSON.parse(rawValue || '[]');
        return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry === 'string' && entry.trim()) : [];
      } catch (error) {
        return [];
      }
    }

    function readViewedProducts() {
      if (!storage) return [];
      try {
        const rawValue = storage.getItem(viewedKey);
        const parsed = JSON.parse(rawValue || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((item) => item && typeof item === 'object' && (item.product_url || item.title));
      } catch (error) {
        return [];
      }
    }

    function writeViewedProducts(items) {
      if (!storage) return;
      try {
        storage.setItem(viewedKey, JSON.stringify(items));
      } catch (error) {
        console.warn('ts home hub viewed save failed:', error);
      }
    }

    function isSameViewedItem(left, right) {
      const leftUrl = String(left?.product_url || left?.url || '').trim();
      const rightUrl = String(right?.product_url || right?.url || '').trim();
      if (leftUrl && rightUrl) return leftUrl === rightUrl;

      const leftProductId = String(left?.product_id || '').trim();
      const rightProductId = String(right?.product_id || '').trim();
      const leftVariantId = String(left?.variant_id || '').trim();
      const rightVariantId = String(right?.variant_id || '').trim();
      if (leftProductId && rightProductId) {
        return leftProductId === rightProductId && leftVariantId === rightVariantId;
      }

      return (
        String(left?.title || '').trim() === String(right?.title || '').trim() &&
        String(left?.ref || '').trim() === String(right?.ref || '').trim()
      );
    }

    function readHomeCardPayload(link) {
      if (!link) return null;

      const imageElement = link.querySelector('img');
      const titleElement = link.querySelector('.ts-home-product-card__title');

        const payload = {
          product_id: String(link.dataset.productId || ''),
          variant_id: String(link.dataset.variantId || ''),
          handle: link.dataset.productHandle || '',
          title: (link.dataset.productTitle || titleElement?.textContent || '').trim(),
          brand: (link.dataset.productBrand || '').trim(),
          model: (link.dataset.productModel || '').trim(),
          source_label: (link.dataset.productSource || '').trim(),
          image: link.dataset.productImage || imageElement?.currentSrc || imageElement?.getAttribute('src') || '',
          price: link.dataset.productPrice || '',
          price_unit: (link.dataset.productPriceUnit || '').trim(),
          sku: '',
          ref: (link.dataset.productRef || '').trim(),
          saved_at: new Date().toISOString(),
          product_url: link.dataset.productUrl || link.getAttribute('href') || '',
        };

      if (!payload.product_id && !payload.product_url && !payload.title) return null;
      return payload;
    }

    function saveViewedProduct(item) {
      if (!item || !storage) return;
      const nextItems = [item, ...readViewedProducts().filter((entry) => !isSameViewedItem(entry, item))].slice(
        0,
        12
      );
      writeViewedProducts(nextItems);
    }

    function saveRecentSearch(query) {
      const normalized = (query || '').trim();
      if (!normalized || !storage) return;

      const nextValues = [normalized, ...readRecentSearches().filter((entry) => entry !== normalized)].slice(
        0,
        MAX_RECENT_SEARCHES
      );

      try {
        storage.setItem(historyKey, JSON.stringify(nextValues));
      } catch (error) {
        console.warn('ts home hub recent search save failed:', error);
      }
    }

    function renderRecentSearches() {
      if (!recentWrap || !recentList) return;

      const searches = readRecentSearches();
      if (!searches.length) {
        recentWrap.hidden = true;
        recentList.innerHTML = '';
        return;
      }

      recentWrap.hidden = false;
      recentList.innerHTML = searches
        .map(
          (entry) =>
            `<button type="button" data-ts-home-search-query="${escapeHtml(entry)}">${escapeHtml(entry)}</button>`
        )
        .join('');
    }

    function renderViewedProducts() {
      if (!viewedSection || !viewedTrack) return;

      const viewedItems = readViewedProducts().slice(0, MAX_VIEWED_PRODUCTS);
      if (!viewedItems.length) {
        viewedSection.hidden = true;
        viewedTrack.innerHTML = '';
        return;
      }

      viewedSection.hidden = false;
      viewedTrack.innerHTML = viewedItems
        .map((item) =>
          buildProductCardMarkup({
            href: item.product_url || item.url || '#',
            image: item.image || '',
            title: item.title || '',
            brand: item.brand || '',
            model: item.model || '',
            sourceLabel: (() => {
              const variantId = String(item.variant_id || '').trim();
              const rawSourceLabel = String(item.sourceLabel || item.source_label || '').trim();
              if (rawSourceLabel && rawSourceLabel !== 'Timeseek') return rawSourceLabel;
              return variantId ? 'Timeseek' : 'マーケット';
            })(),
            reference: item.ref || item.sku || '',
            price: item.price || '',
            priceUnit: item.priceUnit || item.price_unit || '',
            variantId: item.variant_id || '',
            productId: item.product_id || '',
            handle: item.handle || '',
            badgeLabel: item.badgeLabel || '',
            badgeModifier: item.badgeModifier || '',
          })
        )
        .join('');
    }

    function getBuyNowItems(panelId) {
      return panelId === 'timeseek' ? timeseekItems : marketItems;
    }

    function getBuyNowTrack(panelId) {
      return panelId === 'timeseek' ? timeseekTrack : marketTrack;
    }

    function updateBuyNowLinks(panelId) {
      const href = buildCollectionUrl(root, { inventory: panelId });
      buyNowLinks.forEach((link) => { link.href = href; });
    }

    function renderBuyNowPagination(panelId) {
      if (!buyNowPagination) return;

      const items = getBuyNowItems(panelId);
      const totalPages = Math.max(1, Math.ceil(items.length / HOME_BUY_PAGE_SIZE));
      const currentPage = Math.min(Math.max(buyNowPageState[panelId] || 1, 1), totalPages);
      buyNowPageState[panelId] = currentPage;

      if (totalPages <= 1) {
        buyNowPagination.innerHTML = '';
        buyNowPagination.hidden = true;
        return;
      }

      const tokens = buildPaginationTokens(totalPages, currentPage);
      buyNowPagination.hidden = false;
      buyNowPagination.innerHTML = [
        `<button type="button" class="ts-home-buy-now__page-arrow" data-ts-home-buy-page="${currentPage - 1}" ${
          currentPage <= 1 ? 'disabled' : ''
        }>‹</button>`,
        ...tokens.map((token) => {
          if (token === '...') {
            return '<span class="ts-home-buy-now__page-ellipsis">…</span>';
          }

          return `<button type="button" class="ts-home-buy-now__page-btn" data-ts-home-buy-page="${token}" ${
            token === currentPage ? 'aria-current="page"' : ''
          }>${token}</button>`;
        }),
        `<button type="button" class="ts-home-buy-now__page-arrow" data-ts-home-buy-page="${currentPage + 1}" ${
          currentPage >= totalPages ? 'disabled' : ''
        }>›</button>`,
      ].join('');
    }

    function renderBuyNowPanelItems(panelId) {
      const track = getBuyNowTrack(panelId);
      if (!track) return;

      const items = getBuyNowItems(panelId);
      const totalPages = Math.max(1, Math.ceil(items.length / HOME_BUY_PAGE_SIZE));
      const currentPage = Math.min(Math.max(buyNowPageState[panelId] || 1, 1), totalPages);
      buyNowPageState[panelId] = currentPage;

      const marketState = panelId === 'market' ? getHomeMarketState(root) : null;
      if (marketState?.ssrAdopted && track.children?.length === items.length) {
        if (marketStatus) updateHomeMarketNotice(root, marketStatus, marketState);
        renderBuyNowPagination(panelId);
        return;
      }

      if (!items.length) {
        track.innerHTML = '';
        if (panelId === 'market' && marketStatus) {
          marketStatus.hidden = false;
        }
        renderBuyNowPagination(panelId);
        return;
      }

      const start = (currentPage - 1) * HOME_BUY_PAGE_SIZE;
      const visibleItems = items.slice(start, start + HOME_BUY_PAGE_SIZE);
      track.innerHTML = visibleItems.map((item) => buildProductCardMarkup(item)).join('');
      if (panelId === 'market' && marketStatus) {
        marketStatus.hidden = false;
      }
      renderBuyNowPagination(panelId);
    }

    function syncQueryDisplay() {
      if (!queryDisplay) return;
      const hasQuery = currentQuery.trim().length > 0;
      queryDisplay.textContent = hasQuery ? currentQuery.trim() : 'ブランド・モデル・型番を入力';
      queryDisplay.classList.toggle('is-placeholder', !hasQuery);
    }

    function setInventory(nextInventory) {
      currentInventory = nextInventory === 'timeseek' ? 'timeseek' : 'market';
      setPressedButtonState(inventoryButtons, currentInventory, (button) => button.dataset.tsHomeInventoryToggle);
    }

    function navigateToCollection(query = currentQuery) {
      const normalizedQuery = (query || '').trim();
      if (normalizedQuery) {
        saveRecentSearch(normalizedQuery);
      }
      window.location.assign(buildCollectionUrl(root, { query: normalizedQuery, inventory: currentInventory }));
    }

    function closeSheet() {
      if (!sheet || !isSheetOpen) return;
      isSheetOpen = false;
      sheet.hidden = true;
      document.body.classList.remove('ts-home-sheet-open');
      document.removeEventListener('keydown', handleSheetKeydown, true);
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    }

    function handleSheetKeydown(event) {
      if (!isSheetOpen || !dialog) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeSheet();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(dialog);
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    function openSheet() {
      if (!sheet || !dialog) return;
      lastFocusedElement = document.activeElement;
      isSheetOpen = true;
      sheet.hidden = false;
      document.body.classList.add('ts-home-sheet-open');
      renderRecentSearches();
      if (searchInput) {
        searchInput.value = currentQuery;
      }
      document.addEventListener('keydown', handleSheetKeydown, true);
      window.requestAnimationFrame(() => {
        searchInput?.focus();
        searchInput?.select();
      });
    }

    function setExplorePanel(panelId) {
      if (!exploreButtons.length || !explorePanels.length) return;
      setTabButtonState(exploreButtons, panelId, (button) => button.dataset.tsHomePanelTab);
      explorePanels.forEach((panel) => {
        const isActive = panel.dataset.tsHomePanel === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    }

    timeseekItems = Array.from(timeseekTrack?.querySelectorAll('[data-ts-home-product-card]') || [])
      .map((link) => readHomeCardPayload(link))
      .filter(Boolean)
      .map((item) => ({
        href: item.product_url || '#',
        image: item.image || '',
        title: item.title || '',
        brand: item.brand || '',
        model: item.model || '',
        sourceLabel: item.source_label || 'Timeseek',
        reference: item.ref || '',
        price: item.price || '',
        priceUnit: item.price_unit || '',
        variantId: item.variant_id || '',
        productId: item.product_id || '',
        handle: item.handle || '',
      }));

    function ensureMarketCards() {
      if (!marketTrack || !marketStatus) return;
      if (marketLoadPromise) return;

      marketLoadPromise = loadMarketHomeCards(root, marketTrack, marketStatus, (nextItems) => {
        marketItems = Array.isArray(nextItems) ? nextItems : [];
        if (currentBuyNowPanel === 'market') {
          renderBuyNowPanelItems('market');
        }
      }).finally(() => {
        marketLoadPromise = null;
      });
    }

    document.addEventListener?.('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') ensureMarketCards();
    });
    window.addEventListener?.('focus', () => {
      if (document.visibilityState !== 'hidden') ensureMarketCards();
    });

    function setBuyNowPanel(panelId) {
      if (!buyNowButtons.length || !buyNowPanels.length) return;
      const resolvedPanelId = panelId === 'timeseek' ? 'timeseek' : 'market';
      currentBuyNowPanel = resolvedPanelId;

      setTabButtonState(buyNowButtons, resolvedPanelId, (button) => button.dataset.tsHomeBuyTab);
      buyNowPanels.forEach((panel) => {
        const isActive = panel.dataset.tsHomeBuyPanel === resolvedPanelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      updateBuyNowLinks(resolvedPanelId);
      renderBuyNowPanelItems(resolvedPanelId);

      if (resolvedPanelId === 'market') {
        ensureMarketCards();
      }
    }

    inventoryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setInventory(button.dataset.tsHomeInventoryToggle);
      });
    });
    bindArrowKeyNavigation(inventoryButtons, (button) => {
      setInventory(button.dataset.tsHomeInventoryToggle);
    });

    openButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        openSheet();
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closeSheet();
      });
    });

    sheet?.addEventListener('click', (event) => {
      if (event.target === sheet) {
        closeSheet();
      }
    });

    submitButton?.addEventListener('click', (event) => {
      event.preventDefault();
      navigateToCollection();
    });

    searchInput?.addEventListener('input', (event) => {
      currentQuery = event.target.value || '';
      syncQueryDisplay();
    });

    searchForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      currentQuery = searchInput?.value || '';
      syncQueryDisplay();
      navigateToCollection(currentQuery);
    });

    root.addEventListener('click', (event) => {
      const buyNowLink = event.target.closest('[data-ts-home-buy-now-link], [data-ts-home-buy-now-footer-link]');
      if (buyNowLink && root.contains(buyNowLink) && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        window.location.assign(buildCollectionUrl(root, { inventory: currentBuyNowPanel }));
      }

      const productLink = event.target.closest('[data-ts-home-product-card]');
      if (productLink && root.contains(productLink)) {
        const payload = readHomeCardPayload(productLink);
        if (payload) {
          saveViewedProduct(payload);
          renderViewedProducts();
        }
      }

      const paginationButton = event.target.closest('[data-ts-home-buy-page]');
      if (paginationButton && !paginationButton.disabled) {
        event.preventDefault();
        const nextPage = Number.parseInt(paginationButton.dataset.tsHomeBuyPage || '', 10);
        if (Number.isFinite(nextPage) && nextPage > 0) {
          buyNowPageState[currentBuyNowPanel] = nextPage;
          renderBuyNowPanelItems(currentBuyNowPanel);
        }
      }

      const queryButton = event.target.closest('[data-ts-home-search-query]');
      if (queryButton) {
        event.preventDefault();
        currentQuery = queryButton.dataset.tsHomeSearchQuery || '';
        syncQueryDisplay();
        navigateToCollection(currentQuery);
      }
    });

    if (exploreButtons.length) {
      exploreButtons.forEach((button) => {
        button.addEventListener('click', () => {
          setExplorePanel(button.dataset.tsHomePanelTab);
        });
      });
      bindArrowKeyNavigation(exploreButtons, (button) => {
        setExplorePanel(button.dataset.tsHomePanelTab);
      });
      setExplorePanel(exploreButtons[0].dataset.tsHomePanelTab);
    }

    if (buyNowButtons.length) {
      buyNowButtons.forEach((button) => {
        button.addEventListener('click', () => {
          setBuyNowPanel(button.dataset.tsHomeBuyTab);
        });
      });
      bindArrowKeyNavigation(buyNowButtons, (button) => {
        setBuyNowPanel(button.dataset.tsHomeBuyTab);
      });
      setBuyNowPanel(buyNowButtons[0].dataset.tsHomeBuyTab);
    }

    setInventory(currentInventory);
    syncQueryDisplay();
    renderRecentSearches();
    renderViewedProducts();
    ensureMarketCards();
  }

  function init() {
    document.querySelectorAll('[data-ts-home-hub]').forEach((root) => {
      initRoot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
